const DEFAULT_SITE_URL = "https://utdweb.team"
const DEFAULT_ORGANIZATION_NAME = "UTD"

const isString = value => typeof value === "string"

const trimValue = value => (isString(value) ? value.trim() : value)

const isPresent = value => {
  if (value === null || value === undefined) {
    return false
  }

  if (isString(value)) {
    return value.trim() !== ""
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0
  }

  return true
}

export const compactObject = object =>
  Object.entries(object || {}).reduce((acc, [key, value]) => {
    if (!isPresent(value)) {
      return acc
    }

    if (Array.isArray(value)) {
      const items = value.filter(isPresent)

      if (items.length) {
        acc[key] = items
      }

      return acc
    }

    if (typeof value === "object" && !value["@id"]) {
      const compacted = compactObject(value)

      if (isPresent(compacted)) {
        acc[key] = compacted
      }

      return acc
    }

    acc[key] = trimValue(value)

    return acc
  }, {})

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()

const normalizeSiteUrl = siteUrl =>
  (siteUrl || DEFAULT_SITE_URL).replace(/\/+$/, "")

const ensureLeadingSlash = path => {
  if (!path) {
    return "/"
  }

  return path.startsWith("/") ? path : `/${path}`
}

const ensureTrailingSlash = path => {
  if (!path || path === "/") {
    return "/"
  }

  const [pathname, suffix = ""] = path.split(/([?#].*)/)
  const normalizedPathname = pathname.endsWith("/") ? pathname : `${pathname}/`

  return `${normalizedPathname}${suffix}`
}

export const buildAbsoluteUrl = (siteUrl, url) => {
  const value = trimValue(url)

  if (!value) {
    return undefined
  }

  if (/^(https?:|mailto:|tel:)/i.test(value)) {
    return value
  }

  if (value.startsWith("//")) {
    return `https:${value}`
  }

  if (value.startsWith("#")) {
    return `${normalizeSiteUrl(siteUrl)}/${value}`
  }

  return `${normalizeSiteUrl(siteUrl)}${ensureLeadingSlash(value)}`
}

export const buildPageUrl = (siteUrl, path) =>
  buildAbsoluteUrl(siteUrl, ensureTrailingSlash(ensureLeadingSlash(path)))

const buildPagePath = (originPath, currentPage = 1) =>
  `${ensureLeadingSlash(originPath)}${currentPage > 1 ? `/${currentPage}` : ""}`

export const buildImageUrl = (siteUrl, image) => {
  if (!image) {
    return undefined
  }

  if (isString(image)) {
    return buildAbsoluteUrl(siteUrl, image)
  }

  const src =
    image.sourceUrl ||
    image.mediaItemUrl ||
    image.localFile?.publicURL ||
    image.localFile?.childImageSharp?.gatsbyImageData?.images?.fallback?.src ||
    image.childImageSharp?.gatsbyImageData?.images?.fallback?.src

  return buildAbsoluteUrl(siteUrl, src)
}

const graphRef = id => (id ? { "@id": id } : undefined)

const normalizeYear = value =>
  isPresent(value) ? String(value).trim() : undefined

const getOrganizationId = siteUrl => `${normalizeSiteUrl(siteUrl)}/#organization`

const getWebsiteId = siteUrl => `${normalizeSiteUrl(siteUrl)}/#website`

const getWebPageId = (siteUrl, path) => `${buildPageUrl(siteUrl, path)}#webpage`

const getItemListId = (siteUrl, path) => `${buildPageUrl(siteUrl, path)}#itemlist`

const getBreadcrumbId = (siteUrl, path) =>
  `${buildPageUrl(siteUrl, path)}#breadcrumb`

const getOrganizationName = schemaContext =>
  schemaContext?.settings?.companyName ||
  schemaContext?.settings?.websiteName ||
  schemaContext?.siteMetadata?.title ||
  DEFAULT_ORGANIZATION_NAME

const buildOrganizationNode = schemaContext => {
  const { settings = {}, siteUrl } = schemaContext
  const address = settings.address || {}
  const addressNode = compactObject({
    "@type": "PostalAddress",
    streetAddress: address.streetAddress,
    addressLocality: address.addressLocality,
    postalCode: address.postalCode,
    addressCountry: address.addressCountry,
  })
  const logoUrl = buildImageUrl(siteUrl, settings.logo)
  const socialLinks = (settings.socialLinks || [])
    .map(({ url }) => buildAbsoluteUrl(siteUrl, url))
    .filter(Boolean)

  return compactObject({
    "@type": "Organization",
    "@id": getOrganizationId(siteUrl),
    url: buildPageUrl(siteUrl, "/"),
    name: getOrganizationName(schemaContext),
    description: settings.companyDescription,
    logo: logoUrl
      ? {
          "@type": "ImageObject",
          url: logoUrl,
        }
      : undefined,
    sameAs: socialLinks,
    contactPoint: settings.contactEmail
      ? {
          "@type": "ContactPoint",
          email: settings.contactEmail,
        }
      : undefined,
    address: Object.keys(addressNode).length > 1 ? addressNode : undefined,
    foundingDate: settings.foundingDate,
    numberOfEmployees: settings.numberOfEmployees,
  })
}

const buildWebSiteNode = schemaContext => {
  const { settings = {}, siteUrl } = schemaContext

  return compactObject({
    "@type": "WebSite",
    "@id": getWebsiteId(siteUrl),
    url:
      buildAbsoluteUrl(siteUrl, settings.websiteUrl) ||
      buildPageUrl(siteUrl, "/"),
    name: settings.websiteName || getOrganizationName(schemaContext),
    publisher: graphRef(getOrganizationId(siteUrl)),
  })
}

const buildBaseNodes = schemaContext => [
  buildOrganizationNode(schemaContext),
  buildWebSiteNode(schemaContext),
]

const buildPageNode = ({
  schemaContext,
  path,
  type = "WebPage",
  title,
  description,
  mainEntityId,
}) => {
  const { siteUrl } = schemaContext

  return compactObject({
    "@type": type,
    "@id": getWebPageId(siteUrl, path),
    url: buildPageUrl(siteUrl, path),
    name: title,
    description,
    isPartOf: graphRef(getWebsiteId(siteUrl)),
    mainEntity: graphRef(mainEntityId),
  })
}

const buildOfferNode = (schemaContext, offer = {}) => {
  const offerUrl = buildAbsoluteUrl(schemaContext.siteUrl, offer.offerUrl || offer.url)
  const hasOfferData =
    offerUrl || isPresent(offer.price) || offer.availability

  if (!hasOfferData) {
    return undefined
  }

  return compactObject({
    "@type": "Offer",
    url: offerUrl,
    price: offer.price,
    priceCurrency: "USD",
    availability: offer.availability
      ? `https://schema.org/${offer.availability}`
      : undefined,
  })
}

const buildProductNode = ({
  schemaContext,
  path,
  id = "product",
  name,
  description,
  image,
  url,
  releaseDate,
  offer,
}) => {
  const { siteUrl } = schemaContext
  const pageUrl = buildPageUrl(siteUrl, path)

  return compactObject({
    "@type": "Product",
    "@id": `${pageUrl}#${id}`,
    name,
    description,
    image: buildImageUrl(siteUrl, image),
    url: buildAbsoluteUrl(siteUrl, url) || pageUrl,
    brand: {
      "@type": "Brand",
      name: getOrganizationName(schemaContext),
    },
    manufacturer: graphRef(getOrganizationId(siteUrl)),
    releaseDate: normalizeYear(releaseDate),
    offers: buildOfferNode(schemaContext, offer),
  })
}

const buildWebApplicationNode = ({
  schemaContext,
  path,
  id = "app",
  name,
  description,
  image,
  releaseDate,
  offer,
}) => {
  const { siteUrl } = schemaContext
  const pageUrl = buildPageUrl(siteUrl, path)

  return compactObject({
    "@type": "WebApplication",
    "@id": `${pageUrl}#${id}`,
    name,
    description,
    image: buildImageUrl(siteUrl, image),
    url: pageUrl,
    applicationCategory: "BusinessApplication",
    applicationSuite: "Shopify",
    releaseDate: normalizeYear(releaseDate),
    offers: buildOfferNode(schemaContext, offer),
  })
}

const buildCreativeWorkNode = ({
  schemaContext,
  path,
  name,
  description,
  image,
  dateCreated,
  aboutName,
  aboutDescription,
}) => {
  const { siteUrl } = schemaContext
  const pageUrl = buildPageUrl(siteUrl, path)

  return compactObject({
    "@type": "CreativeWork",
    "@id": `${pageUrl}#creativework`,
    name,
    description,
    image: buildImageUrl(siteUrl, image),
    url: pageUrl,
    dateCreated: normalizeYear(dateCreated),
    about: aboutName
      ? compactObject({
          "@type": "Thing",
          name: aboutName,
          description: aboutDescription,
        })
      : undefined,
  })
}

const normalizeAnchor = value => {
  const anchor = trimValue(value)

  if (!anchor) {
    return undefined
  }

  return anchor.replace(/^#/, "")
}

const buildServiceFromSectionNode = ({ schemaContext, path, section }) => {
  const sectionId = normalizeAnchor(section?.sectionId)
  const name = stripHtml(section?.title)
  const description = stripHtml(section?.description)

  if (!sectionId || !name || !description) {
    return undefined
  }

  return compactObject({
    "@type": "Service",
    name,
    description,
    url: `${buildPageUrl(schemaContext.siteUrl, path)}#${sectionId}`,
    areaServed: "Worldwide",
  })
}

const buildItemListNode = ({ schemaContext, path, items, name }) =>
  compactObject({
    "@type": "ItemList",
    "@id": getItemListId(schemaContext.siteUrl, path),
    name,
    numberOfItems: items.length,
    itemListElement: items,
  })

export const buildBreadcrumbListNode = ({ schemaContext, path, items }) => {
  const itemListElement = (items || [])
    .map(({ name, path: itemPath }, index) => {
      const item = buildPageUrl(schemaContext.siteUrl, itemPath)

      if (!name || !item) {
        return null
      }

      return compactObject({
        "@type": "ListItem",
        position: index + 1,
        name,
        item,
      })
    })
    .filter(Boolean)

  if (!itemListElement.length) {
    return undefined
  }

  return compactObject({
    "@type": "BreadcrumbList",
    "@id": getBreadcrumbId(schemaContext.siteUrl, path),
    itemListElement,
  })
}

const buildFAQPageNode = ({ schemaContext, path, questions }) => {
  const mainEntity = (questions || [])
    .map(({ question, answer }) => {
      const name = stripHtml(question)
      const text = stripHtml(answer)

      if (!name || !text) {
        return null
      }

      return compactObject({
        "@type": "Question",
        name,
        acceptedAnswer: {
          "@type": "Answer",
          text,
        },
      })
    })
    .filter(Boolean)

  if (!mainEntity.length) {
    return undefined
  }

  return compactObject({
    "@type": "FAQPage",
    "@id": `${buildPageUrl(schemaContext.siteUrl, path)}#faq`,
    mainEntity,
  })
}

const buildGraph = nodes => {
  const graph = []
  const seenIds = new Set()

  nodes.flat().filter(Boolean).forEach(node => {
    const id = node["@id"]

    if (id && seenIds.has(id)) {
      return
    }

    if (id) {
      seenIds.add(id)
    }

    graph.push(node)
  })

  if (!graph.length) {
    return null
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

const getSection = (sections, sectionName) =>
  (sections || []).find(section => section.fieldGroupName?.includes(sectionName))

const getPageImage = (primaryImage, fallbackImage) => primaryImage || fallbackImage

const getThemeProductData = (schemaContext, theme) => {
  const schema = theme.acfSchemaTheme || {}
  const summary = getSection(theme.acfTheme?.sections, "ThemeSummary")
  const path = `/shopify-themes/${theme.slug}/`

  return {
    schemaContext,
    path,
    name: theme.title,
    description: theme.acfTheme?.metaData?.description,
    image: theme.acfTheme?.squaredImage || theme.acfTheme?.mainImage,
    releaseDate: schema.releaseDate,
    offer: {
      offerUrl: schema.offerUrl || summary?.siteUrl?.url || summary?.buttonLink,
      price: schema.price,
      availability: schema.availability,
    },
  }
}

const getAppData = (schemaContext, app) => {
  const schema = app.acfSchemaApp || {}
  const summary = getSection(app.acfApp?.sections, "AppSummary")
  const path = `/apps/${app.slug}/`
  const description =
    app.acfApp?.metaData?.description ||
    app.acfApp?.description ||
    summary?.description

  return {
    schemaContext,
    path,
    name: app.title,
    description,
    image: getPageImage(schema.applicationImage, app.acfApp?.icon),
    releaseDate: schema.releaseDate,
    offer: {
      offerUrl: schema.offerUrl || summary?.buttonLink,
      price: schema.price,
      availability: schema.availability,
    },
  }
}

const getProjectCreativeWorkData = (schemaContext, project) => {
  const schema = project.acfSchemaProject || {}
  const summary = getSection(project.acfProject?.sections, "ProjectSummary")
  const path = `/projects/${project.slug}/`

  return {
    schemaContext,
    path,
    name: project.title,
    description:
      schema.creativeWorkDescription ||
      project.acfProject?.metaData?.description ||
      summary?.description,
    image: getPageImage(schema.creativeWorkImage, project.acfProject?.mainImage || project.acfProject?.squaredImage),
    dateCreated: schema.dateCreated || summary?.year,
    aboutName: schema.aboutName || summary?.client || project.title,
    aboutDescription:
      schema.aboutDescription ||
      summary?.description ||
      project.acfProject?.metaData?.description,
  }
}

export const buildHomeSchema = ({ schemaContext, title, description }) => {
  const path = "/"
  const organizationId = getOrganizationId(schemaContext.siteUrl)

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: organizationId,
    }),
  ])
}

export const buildAboutPageSchema = ({ schemaContext, title, description, path = "/about-us/" }) => {
  const organizationId = getOrganizationId(schemaContext.siteUrl)

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      type: "AboutPage",
      title,
      description,
      mainEntityId: organizationId,
    }),
  ])
}

export const buildContactPageSchema = ({ schemaContext, title, description, path = "/contact-us/" }) => {
  const organizationId = getOrganizationId(schemaContext.siteUrl)

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      type: "ContactPage",
      title,
      description,
      mainEntityId: organizationId,
    }),
  ])
}

export const buildServicesPageSchema = ({
  schemaContext,
  sections,
  title,
  description,
  path = "/services/",
}) => {
  const serviceNodes = (sections || [])
    .map(section => buildServiceFromSectionNode({ schemaContext, path, section }))
    .filter(service => service?.name)
  const itemListItems = serviceNodes.map((serviceNode, index) =>
    compactObject({
      "@type": "ListItem",
      position: index + 1,
      item: serviceNode,
    })
  )
  const itemList = serviceNodes.length
    ? buildItemListNode({
        schemaContext,
        path,
        items: itemListItems,
        name: "Services by UTD",
      })
    : undefined

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: itemList?.["@id"],
    }),
    itemList,
  ])
}

export const buildShopifyThemesCollectionSchema = ({
  schemaContext,
  themes,
  title,
  description,
}) => {
  const path = "/shopify-themes/"
  const productNodes = (themes || []).map(theme =>
    buildProductNode(getThemeProductData(schemaContext, theme))
  )
  const itemListItems = productNodes.map((productNode, index) =>
    compactObject({
      "@type": "ListItem",
      position: index + 1,
      item: productNode,
    })
  )
  const itemList = buildItemListNode({
    schemaContext,
    path,
    items: itemListItems,
    name: "Shopify Themes by UTD",
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      type: "CollectionPage",
      title,
      description,
      mainEntityId: itemList["@id"],
    }),
    itemList,
  ])
}

export const buildThemePageSchema = ({
  schemaContext,
  theme,
  title,
  description,
}) => {
  const path = `/shopify-themes/${theme.slug}/`
  const productNode = buildProductNode(getThemeProductData(schemaContext, theme))
  const breadcrumbNode = buildBreadcrumbListNode({
    schemaContext,
    path,
    items: [
      { name: "Home", path: "/" },
      { name: "Shopify themes", path: "/shopify-themes/" },
      { name: theme.title, path },
    ],
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: productNode["@id"],
    }),
    productNode,
    breadcrumbNode,
  ])
}

export const buildProjectsCollectionSchema = ({
  schemaContext,
  projects,
  title,
  description,
  currentPage,
  projectsPerPage,
  categoryOriginPath,
}) => {
  const path = buildPagePath(categoryOriginPath, currentPage)
  const startPosition = ((currentPage || 1) - 1) * (projectsPerPage || 0)
  const itemListItems = (projects || []).map((project, index) =>
    compactObject({
      "@type": "ListItem",
      position: startPosition + index + 1,
      name: project.title,
      url: buildPageUrl(schemaContext.siteUrl, `/projects/${project.slug}/`),
    })
  )
  const itemList = buildItemListNode({ schemaContext, path, items: itemListItems })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      type: "CollectionPage",
      title,
      description,
      mainEntityId: itemList["@id"],
    }),
    itemList,
  ])
}

export const buildProjectPageSchema = ({
  schemaContext,
  project,
  title,
  description,
}) => {
  const path = `/projects/${project.slug}/`
  const creativeWorkNode = buildCreativeWorkNode(
    getProjectCreativeWorkData(schemaContext, project)
  )
  const breadcrumbNode = buildBreadcrumbListNode({
    schemaContext,
    path,
    items: [
      { name: "Home", path: "/" },
      { name: "Projects", path: "/projects/" },
      { name: project.title, path },
    ],
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: creativeWorkNode["@id"],
    }),
    creativeWorkNode,
    breadcrumbNode,
  ])
}

export const buildAppsCollectionSchema = ({
  schemaContext,
  apps,
  title,
  description,
  currentPage,
  appsPerPage,
  originPath,
}) => {
  const path = buildPagePath(originPath, currentPage)
  const startPosition = ((currentPage || 1) - 1) * (appsPerPage || 0)
  const appNodes = (apps || []).map(app =>
    buildWebApplicationNode(getAppData(schemaContext, app))
  )
  const itemListItems = appNodes.map((appNode, index) =>
    compactObject({
      "@type": "ListItem",
      position: startPosition + index + 1,
      item: graphRef(appNode["@id"]),
    })
  )
  const itemList = buildItemListNode({ schemaContext, path, items: itemListItems })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      type: "CollectionPage",
      title,
      description,
      mainEntityId: itemList["@id"],
    }),
    itemList,
    appNodes,
  ])
}

export const buildAppPageSchema = ({ schemaContext, app, title, description }) => {
  const path = `/apps/${app.slug}/`
  const appNode = buildWebApplicationNode(getAppData(schemaContext, app))
  const faqSection = getSection(app.acfApp?.sections, "AppFaqs")
  const faqNode = buildFAQPageNode({
    schemaContext,
    path,
    questions: faqSection?.questions,
  })
  const breadcrumbNode = buildBreadcrumbListNode({
    schemaContext,
    path,
    items: [
      { name: "Home", path: "/" },
      { name: "Apps", path: "/apps/" },
      { name: app.title, path },
    ],
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: appNode["@id"],
    }),
    appNode,
    faqNode,
    breadcrumbNode,
  ])
}

export const buildManualPageSchema = ({
  schemaContext,
  manual,
  title,
  description,
}) => {
  const postTypeUri = manual.acfManual?.postType?.uri || "/"
  const path = `${postTypeUri.replace(/\/?$/, "/")}manual/`
  const faqSection = getSection(manual.acfManual?.sections, "AppFaqs")
  const faqNode = buildFAQPageNode({
    schemaContext,
    path,
    questions: faqSection?.questions,
  })

  if (!faqNode) {
    return null
  }

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: faqNode["@id"],
    }),
    faqNode,
  ])
}

export const buildBlogCollectionSchema = ({
  schemaContext,
  title,
  description,
  currentPage,
  categoryOriginPath,
}) => {
  const path = buildPagePath(categoryOriginPath, currentPage)
  const blogId = `${buildPageUrl(schemaContext.siteUrl, path)}#blog`
  const blogNode = compactObject({
    "@type": "Blog",
    "@id": blogId,
    url: buildPageUrl(schemaContext.siteUrl, path),
    name: title,
    description,
    publisher: graphRef(getOrganizationId(schemaContext.siteUrl)),
    isPartOf: graphRef(getWebsiteId(schemaContext.siteUrl)),
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    blogNode,
  ])
}

export const buildBlogPostSchema = ({
  schemaContext,
  blogPost,
  allCategory,
  title,
  description,
}) => {
  const schema = blogPost.acfSchemaPost || {}
  const path = `/blog/${blogPost.slug}/`
  const blogPostingId = `${buildPageUrl(schemaContext.siteUrl, path)}#blogposting`
  const image = schema.blogPostingImage || blogPost.acfPost?.squaredImage || blogPost.acfPost?.banner?.image
  const breadcrumbNode = buildBreadcrumbListNode({
    schemaContext,
    path,
    items: [
      { name: "Home", path: "/" },
      { name: allCategory?.title || "Blog", path: allCategory?.link || "/blog/" },
      { name: blogPost.title, path },
    ],
  })
  const blogPostingNode = compactObject({
    "@type": "BlogPosting",
    "@id": blogPostingId,
    mainEntityOfPage: graphRef(getWebPageId(schemaContext.siteUrl, path)),
    headline: blogPost.title,
    description,
    image: buildImageUrl(schemaContext.siteUrl, image),
    datePublished: blogPost.schemaDate || blogPost.date,
    author: graphRef(getOrganizationId(schemaContext.siteUrl)),
    publisher: graphRef(getOrganizationId(schemaContext.siteUrl)),
  })

  return buildGraph([
    buildBaseNodes(schemaContext),
    buildPageNode({
      schemaContext,
      path,
      title,
      description,
      mainEntityId: blogPostingId,
    }),
    blogPostingNode,
    breadcrumbNode,
  ])
}
